import {
  useNetworkDashboardKpis,
  useNetworkDashboardLiveStoreCa,
  useRealEstateKpis,
  useStoreProjects,
} from '@gozoki/api'
import {
  NETWORK_DASHBOARD_COHORT_UNITS,
  NETWORK_DASHBOARD_STORE_AGE_FILTERS,
  type NetworkDashboardCaDailyAggregate,
  type NetworkDashboardCohortUnit,
  type NetworkDashboardDailyAggregate,
  type NetworkDashboardMergedDailyAggregate,
} from '@gozoki/api-types'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { type JSX, useEffect, useState } from 'react'
import { z } from 'zod'
import { OpenStoresCountCardLink } from '~/components/dashboards/open-stores-count-card'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import { getPercentageOfAnnualCa } from '~/components/routes/development/real-estate/map/map-dashboard-stats'
import {
  computeAverageLandCost,
  computeAverageRent,
} from '~/components/routes/development/real-estate/map/map-kpis'
import type { RealEstateProject } from '~/components/routes/development/real-estate/store-project/types'
import {
  CACard,
  type NetworkDashboardCACardDateWindows,
} from '~/components/routes/network-dashboard/cards/ca-card'
import { CADetailsCard } from '~/components/routes/network-dashboard/cards/ca-details-card'
import { CategoriesCard } from '~/components/routes/network-dashboard/cards/categories-card'
import { CustomerSatifsactionCard } from '~/components/routes/network-dashboard/cards/customer-satifsaction-card'
import { HRCard } from '~/components/routes/network-dashboard/cards/hr-card'
import { NetworkStatCard } from '~/components/routes/network-dashboard/cards/network-stat-card'
import { ProductsCard } from '~/components/routes/network-dashboard/cards/products-card'
import { RealEstateCostsCard } from '~/components/routes/network-dashboard/cards/real-estate-costs-card'
import { UpcomingStoresCard } from '~/components/routes/network-dashboard/cards/upcoming-stores-card'
import {
  CA_DETAIL_SELECTOR_COMPARABLE_INDICATORS,
  CA_DETAIL_SELECTOR_PRINCIPAL_INDICATORS,
  CaModal,
} from '~/components/routes/network-dashboard/modals/ca-modal'
import {
  getDefaultDateRange,
  NETWORK_DASHBOARD_ALL_TIME_START_ISO,
  NetworkFilters,
  type NetworkFiltersRowSelection,
} from '~/components/routes/network-dashboard/network-filters'
import { SelectionPeriodBadge } from '~/components/routes/network-dashboard/selection-period-badge'
import { Button } from '~/components/ui/button'
import { getUpcomingStores } from '~/utils/new-dashboards/upcoming-stores'

function filterAggregatesInRange(
  aggregates: NetworkDashboardDailyAggregate[],
  start: string,
  end: string,
): NetworkDashboardDailyAggregate[] {
  return aggregates.filter((a) => a.date >= start && a.date <= end)
}

function filterCaAggregatesInRange(
  rows: NetworkDashboardCaDailyAggregate[],
  start: string,
  end: string,
): NetworkDashboardCaDailyAggregate[] {
  return rows.filter((a) => a.date >= start && a.date <= end)
}

function mergeOperationalAndCaForCaCard(
  operational: NetworkDashboardDailyAggregate[],
  caRows: NetworkDashboardCaDailyAggregate[],
): NetworkDashboardMergedDailyAggregate[] {
  const caByDate = new Map(caRows.map((r) => [r.date, r]))
  const opDates = new Set(operational.map((r) => r.date))

  const mergedFromOperational: NetworkDashboardMergedDailyAggregate[] = operational.map((row) => {
    const ca = caByDate.get(row.date)
    if (!ca) {
      return { ...row, totalCa: 0, matureCa: 0 }
    }
    return {
      ...row,
      totalCa: ca.totalCa,
      matureCa: ca.matureCa,
      storeCount: ca.storeCount,
      matureStoreCount: ca.matureStoreCount,
    }
  })

  const extras: NetworkDashboardMergedDailyAggregate[] = caRows
    .filter((r) => !opDates.has(r.date))
    .map((ca) => ({
      date: ca.date,
      totalCaHt: 0,
      totalBuyingPrice: 0,
      totalProductsSold: 0,
      totalCartsCount: 0,
      totalStolenCount: 0,
      totalStolenBuyingValue: 0,
      totalDestroyedCount: 0,
      totalDestroyedBuyingValue: 0,
      storeCount: ca.storeCount,
      matureStoreCount: ca.matureStoreCount,
      matureCaHt: 0,
      matureBuyingPrice: 0,
      matureProductsSold: 0,
      matureCartsCount: 0,
      matureStolenCount: 0,
      matureStolenBuyingValue: 0,
      matureDestroyedCount: 0,
      matureDestroyedBuyingValue: 0,
      totalCaByHour: Array.from({ length: 24 }, () => 0),
      totalCartsByHour: Array.from({ length: 24 }, () => 0),
      totalArticlesSoldByHour: Array.from({ length: 24 }, () => 0),
      matureCaByHour: Array.from({ length: 24 }, () => 0),
      matureCartsByHour: Array.from({ length: 24 }, () => 0),
      matureArticlesSoldByHour: Array.from({ length: 24 }, () => 0),
      totalCa: ca.totalCa,
      matureCa: ca.matureCa,
    }))

  return [...mergedFromOperational, ...extras].sort((a, b) => a.date.localeCompare(b.date))
}

function sumCaRows(rows: NetworkDashboardCaDailyAggregate[], useMatureOnly: boolean): number {
  return rows.reduce((sum, r) => sum + (useMatureOnly ? r.matureCa : r.totalCa), 0)
}

function formatNetworkDashboardComparisonPeriodFr(start: string, end: string): string {
  const startISO = DateTime.fromISO(start)
  const endISO = DateTime.fromISO(end)
  if (start === end) {
    return 'J'
  }
  if (startISO.plus({ days: 6 }).equals(endISO)) {
    return 'S'
  }
  if (startISO.plus({ days: 29 }).equals(endISO)) {
    return 'M'
  }
  if (startISO.plus({ days: 364 }).equals(endISO)) {
    return 'A'
  }
  return 'P'
}

function formatCohortBadgeLabel(
  unit: NetworkDashboardCohortUnit | undefined,
  value: number | undefined,
): string {
  if (unit === undefined || value === undefined) {
    return 'Cohorte'
  }
  const unitLabel: Record<NetworkDashboardCohortUnit, string> = {
    day: value === 1 ? 'jour' : 'jours',
    week: value === 1 ? 'semaine' : 'semaines',
    month: 'mois',
    year: value === 1 ? 'année' : 'années',
  }
  return `Cohorte ${value} ${unitLabel[unit]}`
}

function getAggregateValue(
  aggregate: NetworkDashboardDailyAggregate,
  showMatureOnly: boolean,
  matureKey:
    | 'matureCartsCount'
    | 'matureProductsSold'
    | 'matureCaHt'
    | 'matureStolenBuyingValue'
    | 'matureStolenCount'
    | 'matureDestroyedBuyingValue'
    | 'matureDestroyedCount',
  totalKey:
    | 'totalCartsCount'
    | 'totalProductsSold'
    | 'totalCaHt'
    | 'totalStolenBuyingValue'
    | 'totalStolenCount'
    | 'totalDestroyedBuyingValue'
    | 'totalDestroyedCount',
): number {
  if (showMatureOnly) {
    return aggregate[matureKey]
  }

  return aggregate[totalKey]
}

function NetworkDashboardPage(): JSX.Element {
  const { storeIds, storeAgeFilter, ageStoreIds, startDate, endDate, cohortUnit, cohortValue } =
    Route.useSearch()
  const navigate = Route.useNavigate()
  const { data: projects = [] } = useStoreProjects()
  const { data: realEstateKpis } = useRealEstateKpis({ openStoresComparisonDays: 7 })

  const defaultRange = getDefaultDateRange()
  const requestStartDate =
    startDate ??
    (defaultRange.from
      ? DateTime.fromJSDate(defaultRange.from).toISODate()!
      : NETWORK_DASHBOARD_ALL_TIME_START_ISO)
  const requestEndDate = endDate ?? DateTime.fromJSDate(defaultRange.to).toISODate()!

  const isCohortMode = cohortUnit !== undefined && cohortValue !== undefined

  const { data: networkKpis } = useNetworkDashboardKpis(storeIds, storeAgeFilter, {
    ...(isCohortMode
      ? { cohortUnit, cohortValue }
      : { startDate: requestStartDate, endDate: requestEndDate }),
    ...(ageStoreIds !== undefined ? { ageStoreIds } : {}),
  })

  const { data: liveStoreCa } = useNetworkDashboardLiveStoreCa()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCaModalOpen, setIsCaModalOpen] = useState(false)
  const [caModalVariant, setCaModalVariant] = useState<'network' | 'store'>('network')
  const [reopenCaModalAfterFiltersClose, setReopenCaModalAfterFiltersClose] = useState(false)
  const [selectorSource, setSelectorSource] = useState<'header' | 'caModal'>('header')
  const [caDetailIndicatorIds, setCaDetailIndicatorIds] = useState<{
    principal: string
    comparable: string
  } | null>(null)
  const [caDetailComparableSelection, setCaDetailComparableSelection] =
    useState<NetworkFiltersRowSelection | null>(null)

  useEffect(() => {
    if (!isModalOpen && reopenCaModalAfterFiltersClose) {
      setReopenCaModalAfterFiltersClose(false)
      setIsCaModalOpen(true)
    }
  }, [isModalOpen, reopenCaModalAfterFiltersClose])

  const selectedDateRange = {
    from:
      requestStartDate === NETWORK_DASHBOARD_ALL_TIME_START_ISO
        ? null
        : DateTime.fromISO(requestStartDate).toJSDate(),
    to: DateTime.fromISO(requestEndDate).toJSDate(),
  }

  const principalRowSelection: NetworkFiltersRowSelection = {
    storeIds,
    storeAgeFilter,
    ageStoreIds,
    dateRange: selectedDateRange,
    cohort: {
      enabled: isCohortMode,
      unit: cohortUnit ?? 'week',
      value: cohortValue ?? 1,
    },
  }

  // Two effective "show > 3 mois" flags driven by the seniority filter:
  //  - `networkShowMatureOnly`: used for cards whose historical default was
  //    to aggregate over all stores (CA chart total). They only flip to the
  //    mature subset when the user explicitly picks `mature`.
  //  - `matureDefaultShowMatureOnly`: used for cards whose historical
  //    default was to aggregate over mature stores only (store CA, HR,
  //    network stat cards). They stay on the mature subset both for
  //    `default` and `mature`, and only switch to every store for `all`.
  const networkShowMatureOnly = false
  const matureDefaultShowMatureOnly = storeAgeFilter === 'default'

  const dailyAggregates = networkKpis?.dailyAggregates ?? []
  const caDailyAggregates = networkKpis?.caDailyAggregates ?? []
  const mergedCaCardAggregates = mergeOperationalAndCaForCaCard(dailyAggregates, caDailyAggregates)
  const categoryPdm = networkKpis?.categoryPdm ?? []
  const topProducts = networkKpis?.topProducts ?? []

  const periodBounds = networkKpis
    ? {
        periodStart: networkKpis.periodStart,
        periodEnd: networkKpis.periodEnd,
        compStart: networkKpis.compStart,
        compEnd: networkKpis.compEnd,
      }
    : null

  const caTotalsWindows: NetworkDashboardCACardDateWindows | undefined = networkKpis
    ? {
        periodStart: networkKpis.periodStart,
        periodEnd: networkKpis.periodEnd,
        compStart: networkKpis.compStart,
        compEnd: networkKpis.compEnd,
      }
    : undefined

  const caChartWindows: NetworkDashboardCACardDateWindows | undefined = networkKpis
    ? {
        periodStart: networkKpis.caChartMainStart,
        periodEnd: networkKpis.caChartMainEnd,
        compStart: networkKpis.caChartCompStart,
        compEnd: networkKpis.caChartCompEnd,
      }
    : undefined

  const comparisonLabel = networkKpis
    ? `vs ${formatNetworkDashboardComparisonPeriodFr(networkKpis.compStart, networkKpis.compEnd)}-1`
    : 'vs S-1'
  const caChartLegend: { period: string; comparison: string } | undefined = networkKpis
    ? {
        period: `Période (${formatNetworkDashboardComparisonPeriodFr(networkKpis.periodStart, networkKpis.periodEnd)})`,
        comparison: `Comparaison (${formatNetworkDashboardComparisonPeriodFr(networkKpis.compStart, networkKpis.compEnd)}-1)`,
      }
    : undefined

  const currentWeekAggregates = periodBounds
    ? filterAggregatesInRange(dailyAggregates, periodBounds.periodStart, periodBounds.periodEnd)
    : []
  const previousWeekAggregates = periodBounds
    ? filterAggregatesInRange(dailyAggregates, periodBounds.compStart, periodBounds.compEnd)
    : []
  const currentCaRows =
    periodBounds != null
      ? filterCaAggregatesInRange(
          caDailyAggregates,
          periodBounds.periodStart,
          periodBounds.periodEnd,
        )
      : []
  const previousCaRows =
    periodBounds != null
      ? filterCaAggregatesInRange(caDailyAggregates, periodBounds.compStart, periodBounds.compEnd)
      : []

  // The following block feeds the cards whose pre-filter default was mature
  // only (store CA, HR, stat cards). `matureDefaultShowMatureOnly` is true
  // when the override is `default` or `mature`, false only when `all`.
  const currentCaTTC = sumCaRows(currentCaRows, matureDefaultShowMatureOnly)
  const previousCaTTCSelected = sumCaRows(previousCaRows, matureDefaultShowMatureOnly)
  const currentCartsCount = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCartsCount', 'totalCartsCount'),
    0,
  )
  const previousCartsCount = previousWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCartsCount', 'totalCartsCount'),
    0,
  )
  const currentProductsSold = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureProductsSold', 'totalProductsSold'),
    0,
  )

  const currentWeekMerged =
    periodBounds != null
      ? mergedCaCardAggregates.filter(
          (a) => a.date >= periodBounds.periodStart && a.date <= periodBounds.periodEnd,
        )
      : []

  const currentCAPerStore = currentWeekMerged.reduce((sum, a) => {
    const storeCount = matureDefaultShowMatureOnly ? a.matureStoreCount : a.storeCount
    const caValue = matureDefaultShowMatureOnly ? a.matureCa : a.totalCa
    if (storeCount <= 0) return sum
    return sum + caValue / storeCount
  }, 0)

  const hrMetrics = networkKpis?.hrMetrics
  const hrPercentageCA =
    hrMetrics && currentCAPerStore > 0
      ? Math.round((hrMetrics.avgSalaryCostPerStore / currentCAPerStore) * 100)
      : 0

  const panierMoyen = currentCartsCount === 0 ? 0 : currentCaTTC / currentCartsCount
  const previousPanierMoyen =
    previousCartsCount === 0 ? 0 : previousCaTTCSelected / previousCartsCount
  const panierMoyenPercentageChange =
    previousPanierMoyen === 0
      ? 0
      : Math.round(((panierMoyen - previousPanierMoyen) / previousPanierMoyen) * 100)

  const articlesPerCart = currentCartsCount === 0 ? 0 : currentProductsSold / currentCartsCount

  const currentCaHT = currentWeekAggregates.reduce(
    (sum, a) => sum + getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCaHt', 'totalCaHt'),
    0,
  )
  const previousCaHT = previousWeekAggregates.reduce(
    (sum, a) => sum + getAggregateValue(a, matureDefaultShowMatureOnly, 'matureCaHt', 'totalCaHt'),
    0,
  )

  const currentStolenValue = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureStolenBuyingValue',
        'totalStolenBuyingValue',
      ),
    0,
  )
  const previousStolenValue = previousWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureStolenBuyingValue',
        'totalStolenBuyingValue',
      ),
    0,
  )
  const currentStolenCount = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(a, matureDefaultShowMatureOnly, 'matureStolenCount', 'totalStolenCount'),
    0,
  )
  const txVol = currentCaHT > 0 ? (currentStolenValue / currentCaHT) * 100 : 0
  const previousTxVol = previousCaHT > 0 ? (previousStolenValue / previousCaHT) * 100 : 0
  const txVolChange =
    previousTxVol === 0 ? 0 : Math.round(((txVol - previousTxVol) / previousTxVol) * 100)

  const currentDestroyedValue = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureDestroyedBuyingValue',
        'totalDestroyedBuyingValue',
      ),
    0,
  )
  const previousDestroyedValue = previousWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureDestroyedBuyingValue',
        'totalDestroyedBuyingValue',
      ),
    0,
  )
  const currentDestroyedCount = currentWeekAggregates.reduce(
    (sum, a) =>
      sum +
      getAggregateValue(
        a,
        matureDefaultShowMatureOnly,
        'matureDestroyedCount',
        'totalDestroyedCount',
      ),
    0,
  )
  const txCasse = currentCaHT > 0 ? (currentDestroyedValue / currentCaHT) * 100 : 0
  const previousTxCasse = previousCaHT > 0 ? (previousDestroyedValue / previousCaHT) * 100 : 0
  const txCasseChange =
    previousTxCasse === 0 ? 0 : Math.round(((txCasse - previousTxCasse) / previousTxCasse) * 100)

  const delta = realEstateKpis?.openStoresDelta ?? 0
  const openStoresDeltaIndex = `${delta >= 0 ? '+' : ''}${delta} vs S-1`
  const typedProjects = projects as RealEstateProject[]
  const openStoresCount = typedProjects.filter((p) => p.status === 'open' && !p.isAbandoned).length
  const today = new Date()

  const upcomingStores = getUpcomingStores(typedProjects).filter(
    (p) =>
      today <= p.openingDate &&
      p.openingDate <= new Date(today.getTime() + 3 * 3_600_000 * 24 * 30),
  )

  const avgLandCostEur = realEstateKpis?.avgLandCostEur ?? computeAverageLandCost(typedProjects)
  const avgRent = computeAverageRent(typedProjects)
  const avgLandCostPctOfCa = getPercentageOfAnnualCa(
    avgLandCostEur,
    realEstateKpis?.averageAnnualCaEur ?? 0,
  )
  const avgRentPctOfCa = getPercentageOfAnnualCa(avgRent, realEstateKpis?.averageAnnualCaEur ?? 0)

  const liveCaDetailsTotal = liveStoreCa?.networkTotalCa ?? 0
  const liveCaDetailsComparison = liveStoreCa?.networkYesterdayCa ?? 0
  let liveCaDetailsPercentageChange = 0
  if (liveCaDetailsComparison !== 0) {
    liveCaDetailsPercentageChange = Math.round(
      ((liveCaDetailsTotal - liveCaDetailsComparison) / liveCaDetailsComparison) * 100,
    )
  }

  let caModalShowMatureOnly = matureDefaultShowMatureOnly
  if (caModalVariant === 'network') {
    caModalShowMatureOnly = networkShowMatureOnly
  }

  return (
    <>
      <AppHeader className="mb-4">
        <div className="flex items-center gap-2">
          <Title className="text-foreground text-[32px] font-extrabold leading-[normal] font-brand">
            Réseau & Boutiques
          </Title>
          <SelectionPeriodBadge
            startDate={requestStartDate}
            endDate={requestEndDate}
            label={isCohortMode ? formatCohortBadgeLabel(cohortUnit, cohortValue) : undefined}
          />
        </div>
      </AppHeader>

      <AppContainer>
        <div className="flex justify-end">
          <Button
            variant="outline"
            className="w-fit"
            type="button"
            onClick={() => {
              setReopenCaModalAfterFiltersClose(false)
              setSelectorSource('header')
              setIsModalOpen(true)
            }}
          >
            Sélecteur
          </Button>
        </div>

        {isModalOpen && (
          <NetworkFilters
            open={isModalOpen}
            onOpenChange={setIsModalOpen}
            comparisonMode={selectorSource === 'caModal' ? 'dual' : 'none'}
            principalIndicator={
              selectorSource === 'caModal'
                ? {
                    ...CA_DETAIL_SELECTOR_PRINCIPAL_INDICATORS,
                    defaultSelectedId:
                      caDetailIndicatorIds?.principal ??
                      CA_DETAIL_SELECTOR_PRINCIPAL_INDICATORS.defaultSelectedId,
                  }
                : undefined
            }
            comparableIndicator={
              selectorSource === 'caModal'
                ? {
                    ...CA_DETAIL_SELECTOR_COMPARABLE_INDICATORS,
                    defaultSelectedId:
                      caDetailIndicatorIds?.comparable ??
                      CA_DETAIL_SELECTOR_COMPARABLE_INDICATORS.defaultSelectedId,
                  }
                : undefined
            }
            principal={principalRowSelection}
            comparable={
              selectorSource === 'caModal'
                ? (caDetailComparableSelection ?? principalRowSelection)
                : undefined
            }
            availableStores={networkKpis?.availableStores ?? []}
            onApply={(payload) => {
              if (payload.comparisonMode === 'dual') {
                setCaDetailIndicatorIds({
                  principal: payload.principal.indicatorId,
                  comparable: payload.comparable.indicatorId,
                })
                setCaDetailComparableSelection({
                  storeIds: payload.comparable.storeIds,
                  storeAgeFilter: payload.comparable.storeAgeFilter,
                  ageStoreIds: payload.comparable.ageStoreIds,
                  dateRange: payload.comparable.dateRange,
                })
                const p = payload.principal
                void navigate({
                  search: {
                    storeIds: p.storeIds.length > 0 ? p.storeIds : undefined,
                    storeAgeFilter: p.storeAgeFilter === 'default' ? undefined : p.storeAgeFilter,
                    ageStoreIds:
                      p.ageStoreIds !== undefined && p.ageStoreIds.length > 0
                        ? p.ageStoreIds
                        : undefined,
                    startDate: p.dateRange.from
                      ? DateTime.fromJSDate(p.dateRange.from).toISODate()!
                      : NETWORK_DASHBOARD_ALL_TIME_START_ISO,
                    endDate: DateTime.fromJSDate(p.dateRange.to).toISODate()!,
                    cohortUnit: undefined,
                    cohortValue: undefined,
                  },
                })
                setIsModalOpen(false)
                return
              }
              const {
                cohort,
                dateRange,
                storeAgeFilter: nextAge,
                storeIds: nextIds,
                ageStoreIds: nextAgeIds,
              } = payload
              void navigate({
                search: {
                  storeIds: nextIds.length > 0 ? nextIds : undefined,
                  storeAgeFilter: nextAge === 'default' ? undefined : nextAge,
                  ageStoreIds:
                    nextAgeIds !== undefined && nextAgeIds.length > 0 ? nextAgeIds : undefined,
                  startDate:
                    cohort?.enabled === true
                      ? undefined
                      : dateRange.from
                        ? DateTime.fromJSDate(dateRange.from).toISODate()!
                        : NETWORK_DASHBOARD_ALL_TIME_START_ISO,
                  endDate:
                    cohort?.enabled === true
                      ? undefined
                      : DateTime.fromJSDate(dateRange.to).toISODate()!,
                  cohortUnit: cohort?.enabled === true ? cohort.unit : undefined,
                  cohortValue: cohort?.enabled === true ? cohort.value : undefined,
                },
              })
              setIsModalOpen(false)
            }}
          />
        )}

        <CaModal
          open={isCaModalOpen}
          onOpenChange={setIsCaModalOpen}
          startDate={requestStartDate}
          endDate={requestEndDate}
          variant={caModalVariant}
          aggregates={mergedCaCardAggregates}
          showMatureOnly={caModalShowMatureOnly}
          storeAgeFilter={storeAgeFilter}
          isStoreSelectionActive={storeIds.length > 0}
          totalsWindows={caTotalsWindows}
          chartWindows={caChartWindows}
          caChartGranularity={networkKpis?.caChartGranularity ?? 'day'}
          mainIndicatorId={
            caDetailIndicatorIds?.principal ??
            CA_DETAIL_SELECTOR_PRINCIPAL_INDICATORS.defaultSelectedId ??
            'ca'
          }
          comparisonText={comparisonLabel}
          showComparison={!isCohortMode}
          onOpenPeriodSelector={() => {
            setReopenCaModalAfterFiltersClose(true)
            setIsCaModalOpen(false)
            setSelectorSource('caModal')
            setIsModalOpen(true)
          }}
        />

        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: '16rem minmax(0, 1fr) minmax(10rem, 15rem)' }}
        >
          {/* Left column */}
          <div className="max-h-0 min-h-full flex flex-col gap-2">
            <OpenStoresCountCardLink
              openStoresCount={openStoresCount}
              openStoresDeltaIndex={openStoresDeltaIndex}
            />
            <UpcomingStoresCard upcomingStores={upcomingStores} />
            <RealEstateCostsCard
              avgLandCostEur={avgLandCostEur ?? 0}
              avgLandCostPctOfCa={avgLandCostPctOfCa ?? ''}
              avgRent={avgRent ?? 0}
              avgRentPctOfCa={avgRentPctOfCa ?? ''}
            />
            <CustomerSatifsactionCard customerSatifsaction={4.6} />
          </div>

          {/* Center column */}
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex min-w-0 gap-2">
              <div className="flex-1 min-w-0">
                <CACard
                  variant="network"
                  aggregates={mergedCaCardAggregates}
                  showMatureOnly={networkShowMatureOnly}
                  storeAgeFilter={storeAgeFilter}
                  isStoreSelectionActive={storeIds.length > 0}
                  totalsWindows={caTotalsWindows}
                  chartWindows={caChartWindows}
                  caChartGranularity={networkKpis?.caChartGranularity ?? 'day'}
                  comparisonText={comparisonLabel}
                  chartLegend={caChartLegend}
                  showComparison={!isCohortMode}
                  onClick={() => {
                    setCaModalVariant('network')
                    setIsCaModalOpen(true)
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <CACard
                  variant="store"
                  aggregates={mergedCaCardAggregates}
                  showMatureOnly={matureDefaultShowMatureOnly}
                  storeAgeFilter={storeAgeFilter}
                  totalsWindows={caTotalsWindows}
                  chartWindows={caChartWindows}
                  caChartGranularity={networkKpis?.caChartGranularity ?? 'day'}
                  comparisonText={comparisonLabel}
                  chartLegend={caChartLegend}
                  showComparison={!isCohortMode}
                  onClick={() => {
                    setCaModalVariant('store')
                    setIsCaModalOpen(true)
                  }}
                />
              </div>
            </div>
            <div className="flex min-w-0 gap-2">
              <div className="flex min-h-0 min-w-0 flex-[1.2] flex-col">
                <CategoriesCard categories={categoryPdm} storeAgeFilter={storeAgeFilter} />
              </div>
              <div className="flex min-h-0 flex-[0.8] flex-col min-w-0">
                <ProductsCard
                  products={topProducts}
                  categories={categoryPdm}
                  storeAgeFilter={storeAgeFilter}
                  comparisonLabel={comparisonLabel}
                  showComparison={!isCohortMode}
                />
              </div>
            </div>
            <div className="flex min-w-0 gap-2">
              <div className="flex-[0.4] flex">
                <HRCard
                  avgETP={hrMetrics?.avgETP ?? 0}
                  percentageCA={hrPercentageCA}
                  percentageInPerson={hrMetrics?.inPersonHoursTotal ?? 0}
                  percentageRemote={hrMetrics?.remoteHoursTotal ?? 0}
                  storeAgeFilter={storeAgeFilter}
                />
              </div>
              <div className="flex-1 min-w-0 grid grid-cols-3 gap-2">
                <NetworkStatCard
                  title="Tx de casse"
                  value={`${txCasse.toFixed(1)}%`}
                  subText={`${Math.round(currentDestroyedCount)} UVC`}
                  comparisonPercentage={txCasseChange}
                  desiredTrend="down"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={!isCohortMode}
                />
                <NetworkStatCard
                  title="Tx de vol"
                  value={`${txVol.toFixed(1)}%`}
                  subText={`${Math.round(currentStolenCount)} UVC`}
                  comparisonPercentage={txVolChange}
                  desiredTrend="down"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={!isCohortMode}
                />
                <NetworkStatCard
                  title="Autres charges"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="down"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={!isCohortMode}
                />
                <NetworkStatCard
                  title="Tx de service"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="up"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={!isCohortMode}
                />
                <NetworkStatCard
                  title="Panier moyen"
                  value={`${Number(panierMoyen.toFixed(2)).toLocaleString('fr-FR')} €`}
                  subText={`${articlesPerCart.toFixed(1)} A/T`}
                  comparisonPercentage={panierMoyenPercentageChange}
                  desiredTrend="up"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={!isCohortMode}
                />
                <NetworkStatCard
                  variant="primary-light"
                  title="Contribution"
                  value="10%"
                  subText="10 UVC"
                  comparisonPercentage={10}
                  desiredTrend="up"
                  comparisonText={comparisonLabel}
                  storeAgeFilter={storeAgeFilter}
                  showComparison={!isCohortMode}
                />
              </div>
            </div>
          </div>

          {/* Right column — max-h-0 + min-h-full prevents this cell from
             influencing the grid row height while still stretching to fill it */}
          <div className="max-h-0 min-h-full">
            <CADetailsCard
              caTTC={liveCaDetailsTotal}
              caTTCPercentageChange={liveCaDetailsPercentageChange}
              storeWeeklyCas={liveStoreCa?.storeCas ?? []}
              storeAgeFilter="all"
              comparisonText="vs J-1"
            />
          </div>
        </div>
      </AppContainer>
    </>
  )
}

const networkDashboardSearchSchema = z
  .object({
    storeIds: z.array(z.number()).optional().default([]),
    storeAgeFilter: z.enum(NETWORK_DASHBOARD_STORE_AGE_FILTERS).optional().default('default'),
    ageStoreIds: z.array(z.number()).optional(),
    startDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    endDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    cohortUnit: z.enum(NETWORK_DASHBOARD_COHORT_UNITS).optional(),
    cohortValue: z.number().int().positive().optional(),
  })
  .refine(
    (data) =>
      (data.startDate === undefined && data.endDate === undefined) ||
      (data.startDate !== undefined && data.endDate !== undefined),
    { message: 'startDate and endDate must both be set or both omitted', path: ['startDate'] },
  )
  .refine(
    (data) =>
      (data.cohortUnit === undefined && data.cohortValue === undefined) ||
      (data.cohortUnit !== undefined && data.cohortValue !== undefined),
    {
      message: 'cohortUnit and cohortValue must both be set or both omitted',
      path: ['cohortUnit'],
    },
  )
  .refine((data) => data.cohortUnit === undefined || data.startDate === undefined, {
    message: 'cohort params cannot be combined with date params',
    path: ['cohortUnit'],
  })
  .refine(
    (data) => {
      if (data.cohortUnit === undefined || data.cohortValue === undefined) {
        return true
      }
      const limits: Record<NetworkDashboardCohortUnit, { min: number; max: number }> = {
        day: { min: 1, max: 90 },
        week: { min: 1, max: 24 },
        month: { min: 1, max: 24 },
        year: { min: 1, max: 5 },
      }
      const limit = limits[data.cohortUnit]
      return data.cohortValue >= limit.min && data.cohortValue <= limit.max
    },
    { message: 'cohortValue is out of range for cohortUnit', path: ['cohortValue'] },
  )

export type NetworkDashboardSearch = z.infer<typeof networkDashboardSearchSchema>

export const Route = createFileRoute('/_authenticated/network-dashboard/')({
  component: NetworkDashboardPage,
  validateSearch: networkDashboardSearchSchema,
})
